import { logger } from "./log.js";
const log = logger("Controls");
const movementKeys = new Set([
    "KeyW",
    "KeyA",
    "KeyS",
    "KeyD",
    "ArrowUp",
    "ArrowLeft",
    "ArrowDown",
    "ArrowRight",
    "Space",
    "KeyC",
    "ShiftLeft",
    "ShiftRight",
]);

export function attachControls(canvas, view, diagnostic) {
    const listeners = [];
    const on = (target, event, callback) => {
        target.addEventListener(event, callback); listeners.push([target, event, callback]);
    };
    const fitViewport = () => {
        const viewer = canvas.parentElement;
        if (!viewer) return;
        if (!document.fullscreenElement) viewer.style.setProperty('--live-viewport-top',
            `${viewer.getBoundingClientRect().top + window.scrollY}px`);
        viewer.style.setProperty('--live-overlay-height', `${Math.max(80,
            viewer.clientHeight - (viewer.querySelector('#toolbar')?.offsetHeight ?? 0) - 48)}px`);
    };
    on(window, 'resize', fitViewport);
    if (canvas.parentElement) requestAnimationFrame(fitViewport);
    const resize = canvas.parentElement && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fitViewport) : null;
    resize?.observe(canvas.parentElement);
    const toolbar = canvas.parentElement?.querySelector('#toolbar');
    if (toolbar) resize?.observe(toolbar);
    const fullscreen = canvas.parentElement?.querySelector('#fullscreen');
    if (fullscreen) {
        fullscreen.hidden = !canvas.parentElement.requestFullscreen;
        on(fullscreen, 'click', () => {
            const request = document.fullscreenElement ? document.exitFullscreen() : canvas.parentElement.requestFullscreen();
            void request.catch(error => diagnostic(`Fullscreen unavailable: ${error.message}`));
        });
        on(document, 'fullscreenchange', () => {
            const label = document.fullscreenElement === canvas.parentElement ? 'Exit fullscreen' : 'Fullscreen';
            fullscreen.setAttribute('aria-label', label); fullscreen.title = label;
            fullscreen.setAttribute('aria-pressed', String(document.fullscreenElement === canvas.parentElement));
            release(); canvas.focus({ preventScroll: true }); fitViewport();
        });
    }
    let session = 0;
    let requested = false;
    let captured = false;
    const locked = () => document.pointerLockElement === canvas;
    const release = () => {
        if (requested || captured) log.debug("release mouse", { session, captured });
        session++;
        requested = false;
        captured = false;
        view.keys.clear();
        navigator.keyboard?.unlock?.();
        if (locked()) document.exitPointerLock();
    };
    const unavailable = (request) => {
        if (request !== session || !requested || captured) return;
        log.warn("mouse capture unavailable", { session });
        release();
        diagnostic(
            "Mouse capture unavailable. Allow pointer lock in your browser, then click the map to try again.",
        );
    };

    on(canvas, "click", async (event) => {
        if (event.button !== 0 || requested || captured) return;
        canvas.focus({ preventScroll: true });
        requested = true;
        const request = ++session;
        log.debug("request mouse capture", { session });
        try {
            // Input starts only when pointerlockchange confirms capture. Older
            // browsers return void here; newer browsers return a Promise.
            await canvas.requestPointerLock();
        } catch {
            unavailable(request);
        }
    });
    on(canvas, "mousedown", event => {
        if (captured && locked() && [0, 2].includes(event.button)) {
            view.onSpectatorCycle?.(event.button === 0 ? 1 : -1); event.preventDefault();
        }
    });
    on(canvas, "contextmenu", event => { if (locked()) event.preventDefault(); });
    on(document, "pointerlockchange", () => {
        if (locked()) {
            // Escape, blur or a map change can cancel an outstanding request.
            if (!requested) {
                document.exitPointerLock();
                return;
            }
            captured = true;
            // Keyboard Lock can deliver browser-reserved Ctrl+W in fullscreen.
            // Leave Escape unlocked so it always releases capture.
            if (document.fullscreenElement && navigator.keyboard?.lock) {
                const own = session;
                void navigator.keyboard.lock([...movementKeys]).then(() => {
                    if (!captured && session !== own) navigator.keyboard.unlock();
                }).catch(() => {});
            }
            log.debug("mouse captured", { session });
        } else if (captured) {
            release();
            canvas.blur();
        }
    });
    on(document, "pointerlockerror", () => unavailable(session));
    on(canvas, "blur", release);
    on(window, "blur", release);
    on(document, "visibilitychange", () => {
        if (document.hidden) release();
    });
    on(window, "keydown", (event) => {
        if (event.code === "Escape" && requested) {
            release();
            canvas.blur();
        } else if (captured && locked() && movementKeys.has(event.code)) {
            if (!view.keys.has(event.code)) log.debug("flight key pressed", { key: event.code });
            view.keys.add(event.code);
            event.preventDefault();
        }
    });
    on(window, "keyup", (event) => {
        if (view.keys.delete(event.code)) log.debug("flight key released", { key: event.code });
    });
    on(document, "mousemove", (event) => {
        if (!captured || !locked()) return;
        // Source uses Z-up, with positive pitch looking up. Moving right turns
        // toward decreasing yaw; moving down lowers pitch. Never use cursor
        // positions: they stay fixed during capture and stop at screen edges.
        const sensitivity = 0.002 * (view.sensitivity ?? 1);
        view.yaw -= event.movementX * sensitivity;
        view.pitch = Math.max(
            -1.55,
            Math.min(1.55, view.pitch - event.movementY * sensitivity),
        );
    });
    return { release, capture() { canvas.click(); }, dispose() { release(); resize?.disconnect(); for (const [target, event, callback] of listeners) target.removeEventListener(event, callback); listeners.length = 0; } };
}
